import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EddWidget from '@nykaa/cart-shared/wrapper/EDD';
import {
  CookieName,
  deviceTypes,
  ORIGIN_SOURCE,
} from '@nykaa/cart-shared/constants';
import { setCartInfo, setToastMessage } from '@nykaa/cart-shared/store/action';
import { EDD_EXPERIEMENT_HEADER } from '@nykaa/cart-shared/store/constants';
import {
  cartRefresh,
  getAllAddress,
  setAddress,
} from '@nykaa/cart-shared/store/service';
import { setCookie } from '@nykaa/utils/cookies';
import { CART_TEXT_MESSAGES } from '@nykaa/cart-shared/constants/message';
import { EDD_NOT_ITEM_SERVICABLE_ERROR_CODE } from '@nykaa/cart-shared/constants';
import {
  getItemsWithErrorCode,
  getPincodeNotServicableError,
  isPinCodeNonServiceable,
} from '@nykaa/cart-shared/helpers/utils';
import { getAnalyticsData } from '@nykaa/cart-shared/helpers/tracking';
import { CartEvents } from '@nykaa/cart-shared/constants/tracking';
import { pushData } from '@nykaa/data-layer/utils';
import { State } from 'store/types';
import { useIsLoggedIn } from 'hooks/auth';

interface EDDProps {
  isConvenienceFeeEnabled?: boolean;
}
const EDD = ({ isConvenienceFeeEnabled }: EDDProps) => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const cartInfo = useSelector((state: State) => state.cartReducer.cartInfo);
  const { addressDTO, pincode } = cartInfo || {};
  const eddWidget =
    useSelector((state: State) => state.remoteConfig.switches.cartEddWidget) ||
    false;
  const [showModal, setShowModal] = useState(false);
  const [isFirstTimeDeliveryCheck, setIsFirstTimeDeliveryCheck] =
    useState(false);
  const [addresses, setAddresses] = useState([] as any[]);
  const [errMsg, setErrMsg] = useState('');

  const { SOME_ITEMS_IN_YOU_CART_ARE_NON_SERVICEABLE } = CART_TEXT_MESSAGES;

  const deliveryCardHandler = async () => {
    try {
      if (!(pincode || addressDTO?.postcode)) {
        setIsFirstTimeDeliveryCheck(true);
      }
      let addressWidgetData = [] as any[];
      if (isLoggedIn) {
        const params = {
          deviceType: deviceTypes.DESKTOP,
          catalog_tag_filter: __PLATFORM__,
          domain: __PLATFORM__,
          originSource: ORIGIN_SOURCE.CART,
        };
        const addressData = await getAllAddress(params);
        addressWidgetData = addressData?.addresses?.map((address: any) => {
          const {
            postcode,
            customerAddressId,
            city,
            firstName,
            lastName,
            street,
            isSetDefault,
          } = address;
          return {
            postcode,
            addressId: customerAddressId,
            city,
            firstName,
            lastName,
            street,
            is_set_default: isSetDefault,
            isSelected: isSetDefault,
          };
        });
      }
      setAddresses(addressWidgetData);
      setTimeout(() => {
        setShowModal(true);
      }, 50);
    } catch (error) {
      const { message } = error as Error;
      setToastMessage(message)(dispatch);
      setIsFirstTimeDeliveryCheck(false);
    }
  };

  const addressChangeHandler = async (address: any) => {
    const { addressId, postcode } = address || {};
    const setAddressParams = {
      addressId: addressId || '',
      pincode,
      headers: { [EDD_EXPERIEMENT_HEADER]: eddWidget },
      originSource: ORIGIN_SOURCE.CART,
      deviceType: deviceTypes.DESKTOP,
      catalog_tag_filter: __PLATFORM__,
      domain: __PLATFORM__,
    };
    try {
      await setAddress(setAddressParams);
      setCookie(CookieName.PIN_CODE_FOR_DELIVERY, postcode || '', 30);
      setCookie(CookieName.ADDRESS_ID_FOR_DELIVERY, addressId || '', 30);
    } catch (error) {
      const { message } = error as Error;
      setToastMessage(message)(dispatch);
    } finally {
      setShowModal(false);
      setIsFirstTimeDeliveryCheck(false);
    }
  };

  const handlePinCodeChange = async (pincode: string, onError: Function) => {
    try {
      const params = {
        deviceType: deviceTypes.DESKTOP,
        catalog_tag_filter: __PLATFORM__,
        domain: __PLATFORM__,
        pincode,
        originSource: ORIGIN_SOURCE.CART,
        headers: { [EDD_EXPERIEMENT_HEADER]: eddWidget },
      };
      const data = await cartRefresh(params, isConvenienceFeeEnabled);
      const payload = getAnalyticsData(CartEvents.EDD_CHANGE_CLICK, data);
      pushData(payload.gtmData);

      let analyticsData = getAnalyticsData(
        CartEvents.CART_CHANGE_DELIVERY_PINCODE,
        data
      );
      if (isFirstTimeDeliveryCheck) {
        analyticsData = getAnalyticsData(CartEvents.CART_CHECK_DELIVERY, data);
      }
      pushData(analyticsData.gtmData);
      if (isPinCodeNonServiceable(data)) {
        const errorItem = getItemsWithErrorCode(
          data.items,
          EDD_NOT_ITEM_SERVICABLE_ERROR_CODE
        )[0];
        if (errorItem) {
          setErrMsg(SOME_ITEMS_IN_YOU_CART_ARE_NON_SERVICEABLE);
          setShowModal(false);
        } else {
          onError(getPincodeNotServicableError(data));
        }
        return;
      }
      setShowModal(false);
      setCookie(CookieName.PIN_CODE_FOR_DELIVERY, pincode || '', 30);
      setCookie(CookieName.ADDRESS_ID_FOR_DELIVERY, '', 30);
      setIsFirstTimeDeliveryCheck(false);
      setCartInfo(data)(dispatch);
    } catch (error) {
      const { message } = error as Error;
      setToastMessage(message)(dispatch);
    }
  };

  return (
    <EddWidget
      cartInfo={cartInfo}
      showModal={showModal}
      addresses={addresses}
      errMsg={errMsg}
      deliveryCardHandler={deliveryCardHandler}
      addressChangeHandler={addressChangeHandler}
      handlePinCodeChange={handlePinCodeChange}
      setShowModal={setShowModal}
    />
  );
};

export default EDD;
